import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";

import {
  selectDeleteTrainModelPending,
  selectSelectedTrainModel,
  selectUpdateTrainModelLoading,
} from "../../store/slices/model/slice";
import {
  getTrainModel,
  updateTrainModel,
  deleteTrainModel,
} from "../../store/slices/model/asyncThunks";

import {
  BackButton,
  ModelTitle,
  ConfigSection,
  TutorialLink,
  SectionTitle,
  Label,
  TextInput,
  RequirementsSection,
  RequirementText,
  UploadSection,
  UploadText,
  UploadCount,
  ActionButtons,
  DeleteButton,
  InputContainer,
  Row,
  Text,
  LabelContainer,
  LabelUpload,
  LabelRow,
  FilePreviewGrid,
  FilePreviewItem,
  FilePreview,
  RemoveFileButton,
  FileName,
  SkeletonLoader,
  SkeletonSection,
  SkeletonLoaderColumn,
  UploadCard2,
  UploadDescriptionText,
  ImgContainer,
  RowSpaceBetween,
} from "./TrainingPage.styles";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as StarSvg } from "../../assets/icons/star.svg";
import { ReactComponent as ImagesSvg } from "../../assets/icons/AddMedia.svg";
import { ReactComponent as UploadSvg } from "../../assets/icons/upload.svg";
import { ReactComponent as TrashSvg } from "../../assets/icons/trash.svg";
import { ReactComponent as MagicStickSvg } from "../../assets/icons/magicStick.svg";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import Button from "../../components/utils/Button/Button";
import { toast } from "../../components/modern-ui/sonner";
import GalleryModal from "../../components/GalleryModal/GalleryModal";
import modelService from "../../api/services/model";
import TrainingProgressComponent from "../../components/utils/TrainingProgressComponent/TrainingProgressComponent";
import { selectUser } from "../../store/slices/user/slice";

const TrainingPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const trainModelId = params.modelId;
  const selectedTrainModel = useSelector(selectSelectedTrainModel);
  const updateTrainModelLoading = useSelector(selectUpdateTrainModelLoading);
  const deleteTrainModelLoading = useSelector(selectDeleteTrainModelPending);
  const isLoggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);
  const [prompt, setPrompt] = useState("");
  const [taskName, setTaskName] = useState("");
  const [uploadedVideoFiles, setUploadedVideoFiles] = useState([]);
  const [uploadedImageFiles, setUploadedImageFiles] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [areDataLoadedFromLocalStorage, setAreDataLoadedFromLocalStorage] =
    useState(0);

  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isMediaUploading, setIsMediaUploading] = useState(false);

  useEffect(() => {
    if (isLoggedIn && trainModelId) {
      dispatch(getTrainModel(trainModelId));
    }
  }, [trainModelId, isLoggedIn, dispatch]);

  useEffect(() => {
    if (updateTrainModelLoading) {
      setIsReadOnlyMode(true);
    }
  }, [updateTrainModelLoading]);

  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (!areDataLoadedFromLocalStorage || !trainModelId) return;
    if (!hasSyncedRef.current) {
      hasSyncedRef.current = true;
      return;
    }

    localStorage.setItem(
      "train-files-" + trainModelId,
      JSON.stringify({
        imagesUrl: uploadedImageFiles
          .filter((v) => v.isUploaded && v.serverUrl)
          .map((v) => v.serverUrl),
        selectedImages: selectedImages,
        videosUrl: uploadedVideoFiles
          .filter((v) => v.isUploaded && v.serverUrl)
          .map((v) => v.serverUrl),
        prompt,
        taskName,
      })
    );
  }, [
    areDataLoadedFromLocalStorage,
    prompt,
    selectedImages,
    taskName,
    trainModelId,
    uploadedImageFiles,
    uploadedVideoFiles,
  ]);

  useEffect(() => {
    if (
      selectedTrainModel.data &&
      trainModelId === selectedTrainModel.trainModelId
    ) {
      const model = selectedTrainModel.data;

      const hasExistingFiles =
        (model.imageUrls && model.imageUrls.length > 0) ||
        (model.videoUrls && model.videoUrls.length > 0);

      setIsReadOnlyMode(hasExistingFiles);

      const existingImageFilesFromServer = [];
      const existingVideoFilesFromServer = [];

      if (hasExistingFiles) {
        localStorage.removeItem("train-files-" + trainModelId);
        setPrompt(model.prompt || "");
        setTaskName(model.name || "");
        if (model.imageUrls && model.imageUrls.length > 0) {
          model.imageUrls.forEach((url, index) => {
            existingImageFilesFromServer.push({
              id: `existing-image-${index}`,
              file: null,
              name: `Image ${index + 1}`,
              type: "image/jpeg",
              isExisting: true,
              serverUrl: url,
              isUploaded: true,
            });
          });
        }

        if (model.videoUrls && model.videoUrls.length > 0) {
          model.videoUrls.forEach((url, index) => {
            existingVideoFilesFromServer.push({
              id: `existing-video-${index}`,
              file: null,
              name: `Video ${index + 1}`,
              type: "video/mp4",
              isExisting: true,
              serverUrl: url,
              isUploaded: true,
            });
          });
        }

        setUploadedImageFiles(existingImageFilesFromServer);
        setUploadedVideoFiles(existingVideoFilesFromServer);
      } else {
        const savedFiles = JSON.parse(
          localStorage.getItem("train-files-" + model._id)
        );

        if (
          !uploadedImageFiles.length &&
          !uploadedVideoFiles.length &&
          !selectedImages.length &&
          !areDataLoadedFromLocalStorage &&
          !prompt.length &&
          !taskName.length
        ) {
          if (savedFiles && savedFiles.imagesUrl) {
            savedFiles.imagesUrl.forEach((url, index) => {
              existingImageFilesFromServer.push({
                id: `existing-image-${index}`,
                file: null,
                name: `Image ${index + 1}`,
                type: "image/jpeg",
                isExisting: true,
                serverUrl: url,
                isUploaded: true,
              });
            });
          }

          if (savedFiles && savedFiles.videosUrl) {
            savedFiles.videosUrl.forEach((url, index) => {
              existingVideoFilesFromServer.push({
                id: `existing-video-${index}`,
                file: null,
                name: `Video ${index + 1}`,
                type: "video/mp4",
                isExisting: true,
                serverUrl: url,
                isUploaded: true,
              });
            });
          }

          setSelectedImages(savedFiles?.selectedImages ?? []);
          setUploadedImageFiles(existingImageFilesFromServer);
          setUploadedVideoFiles(existingVideoFilesFromServer);
          setPrompt(savedFiles?.prompt ?? "");
          setTaskName(savedFiles?.taskName ?? "");

          setAreDataLoadedFromLocalStorage(true);
        }
      }
    }
  }, [
    areDataLoadedFromLocalStorage,
    prompt.length,
    selectedImages,
    selectedTrainModel.data,
    selectedTrainModel.trainModelId,
    taskName.length,
    trainModelId,
    uploadedImageFiles.length,
    uploadedVideoFiles.length,
  ]);

  const modelName = selectedTrainModel.data?.name || "";

  useEffect(() => {
    if (!selectedTrainModel.loading) {
      setTimeout(() => {
        setIsLoading(selectedTrainModel.loading);
      }, 300);
    } else {
      setIsLoading(true);
    }
  }, [selectedTrainModel.loading]);

  const handleBack = () => {
    navigate("/train");

    setTimeout(() => {
      setPrompt("");
      setTaskName("");
      setUploadedVideoFiles([]);
      setUploadedImageFiles([]);
      setIsDeleteModalOpen(false);
      setIsReadOnlyMode(false);
    }, 300);
  };

  const handleTutorialClick = () => {
    navigate("/train-tutorial");
  };

  const MAX_FILE_SIZE_MB = 200;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFileUpload = async (event, fileType) => {
    if (isReadOnlyMode) return;

    setIsMediaUploading(true);

    const input = event.target;
    const files = Array.from(input.files);

    // ✅ Filter valid files under 20MB
    const validFiles = files.filter((file) => file.size <= MAX_FILE_SIZE_BYTES);
    const skippedFiles = files.length - validFiles.length;

    if (skippedFiles > 0) {
      toast.error(
        `${skippedFiles} file(s) were skipped because they exceed ${MAX_FILE_SIZE_MB} MB.`
      );
    }

    if (validFiles.length === 0) {
      setIsMediaUploading(false);

      return;
    }

    const newFiles = await Promise.all(
      validFiles.map(async (file) => {
        return {
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          type: file.type,
          isExisting: false,
          isUploaded: false,
        };
      })
    );

    if (fileType === "image") {
      setUploadedImageFiles((prev) => {
        const updated = [...prev, ...newFiles];

        newFiles.forEach(({ file, type, id }) => {
          uploadDataToStorage(file, type, id);
        });

        return updated;
      });
    } else if (fileType === "video") {
      setUploadedVideoFiles((prev) => {
        const updated = [...prev, ...newFiles];

        newFiles.forEach(({ file, type, id }) => {
          uploadDataToStorage(file, type, id);
        });

        return updated;
      });
    }

    setIsMediaUploading(false);

    if (input) input.value = "";
  };

  const handleUploadVideos = () => {
    if (isReadOnlyMode || isMediaUploading) {
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "video/mp4";
    input.onchange = (e) => handleFileUpload(e, "video");
    input.click();
  };

  const handleSelectImages = () => {
    if (isReadOnlyMode) {
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/jpeg, image/png";
    input.onchange = (e) => handleFileUpload(e, "image");
    input.click();
  };

  const handleRemoveFile = (fileId, fileType) => {
    if (isReadOnlyMode) {
      return;
    }

    if (fileType === "video") {
      setUploadedVideoFiles((prev) => {
        const updatedFiles = prev.filter((f) => f.id !== fileId);

        return updatedFiles;
      });
    } else if (fileType === "image") {
      setUploadedImageFiles((prev) => {
        const updatedFiles = prev.filter((f) => f.id !== fileId);
        return updatedFiles;
      });
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteTrainModel(trainModelId)).unwrap();
      navigate("/train");
      localStorage.setItem("activeTabIndex", "2");
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Failed to delete train model:", error);
    }
    setIsDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleStartTraining = async () => {
    try {
      const newVideoFiles = uploadedVideoFiles
        .filter((f) => f.isUploaded && f.serverUrl)
        .map((f) => f.serverUrl.split("?")[0]);

      const newImageFiles = uploadedImageFiles
        .filter((f) => f.isUploaded && f.serverUrl)
        .map((f) => f.serverUrl.split("?")[0]);

      dispatch(
        updateTrainModel({
          trainModelId,
          name: taskName,
          videosUrl: newVideoFiles,
          imagesUrl: [...selectedImages, ...newImageFiles],
          prompt,
        })
      )
        .unwrap()
        .then(() => {
          navigate("/train");
        })
        .catch((err) => {
          console.log(err);
          toast.error(err);
          setIsReadOnlyMode(false);
        });
    } catch (error) {
      console.error("Failed to start training:", error);
    }
  };

  const uploadDataToStorage = async (file, mimeType, id) => {
    try {
      const parts = mimeType.split("/");
      const fileType = parts[0] || null;
      const extension = parts[1] || null;

      const { uploadUrl, fileUrl } = await modelService.getUploadUrl({
        trainModelId,
        fileType,
        extension,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": mimeType,
          "Content-Disposition": "inline",
          "Cache-Control": "public, max-age=2592000, immutable",
        },
        body: file,
      });

      if (!uploadResponse.ok) throw new Error("Upload failed");

      markFileAsUploaded(fileType, id, fileUrl);
      console.log("✅ File uploaded");
    } catch (error) {
      toast.error(error.message);

      const parts = mimeType.split("/");
      const fileType = parts[0] || null;

      removeFile(fileType, id);
    }
  };

  const markFileAsUploaded = (fileType, id, fileUrl) => {
    if (fileType === "image") {
      markImageAsUploaded(id, fileUrl);
    } else {
      markVideoAsUploaded(id, fileUrl);
    }
  };

  const markVideoAsUploaded = (id, fileUrl) => {
    setUploadedVideoFiles((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, isUploaded: true, serverUrl: fileUrl } : f
      )
    );
  };

  const markImageAsUploaded = (id, fileUrl) => {
    setUploadedImageFiles((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, isUploaded: true, serverUrl: fileUrl } : f
      )
    );
  };

  const removeFile = (fileType, id) => {
    if (fileType === "image") {
      removeImageFile(id);
    } else {
      removeVideoFile(id);
    }
  };

  const removeVideoFile = (id) => {
    setUploadedVideoFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const removeImageFile = (id) => {
    setUploadedImageFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Show loading state
  if (isLoading) {
    return (
      <MainContainer>
        <BackButton onClick={handleBack}>
          <ChevronLeft />
          Back to Train
        </BackButton>
        <SkeletonLoaderColumn>
          <SkeletonLoader $height="26px" $width="200px" $radius="4px" />
          <SkeletonLoader $height="26px" $width="400px" $radius="4px" />
          <RowSpaceBetween>
            <SkeletonLoader $height="16px" $width="600px" $radius="4px" />
            <SkeletonLoader $height="20px" $width="40px" $radius="4px" />
          </RowSpaceBetween>
          <SkeletonLoader $height="5px" $width="100%" $radius="100px" />
          <Row>
            <SkeletonSection $height="335px">
              <SkeletonLoader $height="22px" $width="150px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
            </SkeletonSection>

            <SkeletonSection $height="335px">
              <SkeletonLoader $height="22px" $width="180px" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
            </SkeletonSection>
          </Row>

          <SkeletonSection $height="229px">
            <SkeletonLoader $height="27px" $width="140px" />
            <SkeletonLoader
              style={{ marginTop: 16 }}
              $height="136px"
              $width="100%"
              $radius="16px"
            />
          </SkeletonSection>

          <SkeletonSection $height="229px">
            <SkeletonLoader $height="27px" $width="140px" />
            <SkeletonLoader
              style={{ marginTop: 16 }}
              $height="136px"
              $width="100%"
              $radius="16px"
            />
          </SkeletonSection>
        </SkeletonLoaderColumn>
      </MainContainer>
    );
  }

  const totalVideoCount = uploadedVideoFiles.length;
  const totalImageCount = uploadedImageFiles.length + selectedImages.length;

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft />
        Back to Train
      </BackButton>

      <ModelTitle>{modelName}</ModelTitle>
      {totalImageCount > 0 && totalVideoCount > 0 && (
        <TrainingProgressComponent
          progress={50}
          title="Training in Progress"
          subtitle="Training typically takes 24 hours to complete (maybe longer)."
        />
      )}
      <Row>
        <ConfigSection>
          <SectionTitle>Task Configuration</SectionTitle>
          <TutorialLink onClick={handleTutorialClick}>
            📚 View our training tutorial to get started.
          </TutorialLink>

          <InputContainer>
            <Label>Task name</Label>
            <TextInput
              type="text"
              placeholder="New Model 01"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              disabled={isReadOnlyMode}
            />
          </InputContainer>

          <InputContainer>
            <Label>Prompt</Label>
            <TextInput
              type="text"
              placeholder="e.g. 'The woman turns around and walks away.'"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isReadOnlyMode}
            />
            <RequirementText>
              A sentence that clearly and accurately describes the motion in
              your training videos.
            </RequirementText>
          </InputContainer>
        </ConfigSection>

        <RequirementsSection>
          <SectionTitle>Requirements</SectionTitle>

          <LabelContainer>
            <Label>Training Videos</Label>
            <UploadCount>10 - 20 videos</UploadCount>
          </LabelContainer>

          <Text>
            Upload 10 - 20 videos showing the motion you want to train. Each
            video should be between 3 to 5 seconds long, with a minimum
            resolution of 360p and a maximum size of 20MB.
          </Text>

          <LabelContainer>
            <Label>Testing Images</Label>
            <UploadCount>3 - 6 images</UploadCount>
          </LabelContainer>
          <Text>
            Select 3 - 6 images from the library to test the trained model.
          </Text>

          <LabelContainer>
            <Label>Cost</Label>
            <UploadCount>
              <StarSvg />
              {!user?.hasTrainedModel
                ? "720 Credits (40% Off)"
                : "1200 Credits"}
            </UploadCount>
          </LabelContainer>
          <Text>
            Training cost is deducted when you start the training.
            <br />
            Each User has only one opportunity for a discounted trial.
          </Text>
        </RequirementsSection>
      </Row>

      <UploadSection>
        <LabelRow>
          <LabelUpload>Training Videos </LabelUpload>
          <UploadCount>{totalVideoCount}/20</UploadCount>
        </LabelRow>

        {totalVideoCount > 0 && (
          <FilePreviewGrid>
            {uploadedVideoFiles.map((fileObj) => (
              <FilePreviewItem id={fileObj.id} key={fileObj.id}>
                <FilePreview>
                  {!fileObj.isUploaded ? (
                    <ImgContainer>
                      {!fileObj.isUploaded && (
                        <l-line-spinner
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                          size="30"
                          stroke="2"
                          speed="1"
                          color="#fff"
                        />
                      )}
                    </ImgContainer>
                  ) : (
                    <video
                      src={fileObj.serverUrl}
                      muted
                      controls
                      preload="metadata"
                      width="100%"
                      height="auto"
                    />
                  )}
                </FilePreview>
                {!isReadOnlyMode && (
                  <RemoveFileButton
                    onClick={() => handleRemoveFile(fileObj.id, "video")}
                  >
                    <CloseSvg width={12} height={12} />
                  </RemoveFileButton>
                )}
                <FileName>{fileObj.name}</FileName>
              </FilePreviewItem>
            ))}
          </FilePreviewGrid>
        )}

        {!isReadOnlyMode && (
          <UploadCard2 onClick={handleUploadVideos}>
            {isMediaUploading ? (
              <l-line-spinner
                style={{}}
                size="30"
                stroke="2"
                speed="1"
                color="#fff"
              />
            ) : (
              <>
                <UploadSvg />
                <UploadText>Upload Videos</UploadText>
                <UploadDescriptionText>or drag and drop</UploadDescriptionText>
              </>
            )}
          </UploadCard2>
        )}
      </UploadSection>

      <UploadSection>
        <LabelRow>
          <LabelUpload>Training Images</LabelUpload>
          <UploadCount>{totalImageCount}/6</UploadCount>
        </LabelRow>

        {totalImageCount > 0 && (
          <FilePreviewGrid>
            {uploadedImageFiles.map((fileObj) => (
              <FilePreviewItem key={fileObj.id}>
                <FilePreview>
                  <ImgContainer>
                    {!fileObj.isUploaded ? (
                      <l-line-spinner
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                        size="30"
                        stroke="2"
                        speed="1"
                        color="#fff"
                      />
                    ) : (
                      <img src={fileObj.serverUrl} alt={fileObj.name} />
                    )}
                  </ImgContainer>
                </FilePreview>
                {!isReadOnlyMode && (
                  <RemoveFileButton
                    onClick={() => handleRemoveFile(fileObj.id, "image")}
                  >
                    <CloseSvg width={12} height={12} />
                  </RemoveFileButton>
                )}
                <FileName>{fileObj.name}</FileName>
              </FilePreviewItem>
            ))}
            {selectedImages.map((url) => (
              <FilePreviewItem key={url}>
                <FilePreview>
                  <img src={url} alt={url} />
                </FilePreview>
                {!isReadOnlyMode && (
                  <RemoveFileButton
                    onClick={() =>
                      setSelectedImages((prev) =>
                        prev.filter((itemUrl) => url !== itemUrl)
                      )
                    }
                  >
                    <CloseSvg width={12} height={12} />
                  </RemoveFileButton>
                )}
              </FilePreviewItem>
            ))}
          </FilePreviewGrid>
        )}

        {!isReadOnlyMode && (
          <UploadCard2>
            {isMediaUploading ? (
              <l-line-spinner
                style={{}}
                size="30"
                stroke="2"
                speed="1"
                color="#fff"
              />
            ) : (
              <>
                <ImagesSvg />
                <UploadText onClick={() => setShowGalleryModal(true)}>
                  Select Images
                </UploadText>
                <UploadDescriptionText>or</UploadDescriptionText>
                <UploadText onClick={handleSelectImages}>
                  Upload Images
                </UploadText>
                <UploadDescriptionText>
                  .png, .jpeg, .webp
                </UploadDescriptionText>
              </>
            )}
          </UploadCard2>
        )}
      </UploadSection>

      <ActionButtons>
        {(!isReadOnlyMode || updateTrainModelLoading) && (
          <DeleteButton
            leftIcon={<TrashSvg width={15} height={15} />}
            title={"Delete"}
            style={{
              borderRadius: "100px",
              display: "flex",
              width: "130px",
              height: "44px",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#f2efe8",
              cursor: "pointer",
              fontFamily: "DM Sans, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              letterSpacing: "-0.08px",
              transition: "all 0.3s ease-in-out",
            }}
            onClick={handleDelete}
            disabled={updateTrainModelLoading}
          ></DeleteButton>
        )}
        {(!isReadOnlyMode || updateTrainModelLoading) && (
          <Button
            onClick={handleStartTraining}
            disabled={
              updateTrainModelLoading ||
              uploadedImageFiles.filter((f) => f.isUploaded).length +
                selectedImages.length <
                3 ||
              uploadedImageFiles.filter((f) => f.isUploaded).length +
                selectedImages.length >
                6 ||
              uploadedVideoFiles.filter((f) => f.isUploaded).length < 10 ||
              uploadedVideoFiles.filter((f) => f.isUploaded).length > 20 ||
              prompt.length < 1 ||
              taskName.length < 1
            }
            loading={updateTrainModelLoading}
            title={"Start Training"}
            leftIcon={<MagicStickSvg />}
          />
        )}
      </ActionButtons>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Model"
        isLoading={deleteTrainModelLoading}
        message="Are you sure you want to delete this train model? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      <GalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        onConfirm={(images) => setSelectedImages(images)}
        selectedImages={selectedImages}
        uploadedImagesCount={uploadedImageFiles.length}
      />
    </MainContainer>
  );
};

export default TrainingPage;
